import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, useMediaQuery } from "@mui/material";
import PrintTypeSelector from "./components/PrintTypeSelector";
import useBridgeStatus from "./shared/hooks/useBridgeStatus";
import PrinterStatusAlert from "./components/PrinterStatusAlert";
import PrintControls from "./shared/components/PrintControls";
import OrderModals from "./components/OrderModals";
import { useOrderPrint } from "./shared/hooks/useOrderPrint";
import OrderFormContainer from "./components/OrderFormContainer";
import OrdersList from "./components/OrdersList";
import OrderDetails from "./components/OrderDetails";
import { usePrintType } from "./hooks/usePrintType";
import { useOrderSelection } from "./hooks/useOrderSelection";
import { useOrderEanValidation } from "./hooks/useOrderEanValidation";
import { useOrderPrinter } from "./hooks/useOrderPrinter";
import { useSnackbar } from "./hooks/useSnackbar";
import { useOrderPrintManager } from "./hooks/useOrderPrintManager";
import {
  useGetMarketplaceOrders,
  useGetMarketplaceInvoice,
  useGetMarketplaceInvoiceLabel,
  useGetMarketplaceChannels,
  useMarkInvoiceAsPrinted,
} from "../../client/hooks/marketplace/marketplace.queries";
import { playBeep } from "../../utils/audio";

const PrintOrderLabel = () => {
  const [labelFormat, setLabelFormat] = useState("zpl");
  const [filter, setFilter] = useState("today");
  const [channelFilter, setChannelFilter] = useState("all");
  const [reprintDialogOpen, setReprintDialogOpen] = useState(false);
  const [pendingReprintOrder, setPendingReprintOrder] = useState(null);
  const theme = useTheme();

  const { data: channelsData } = useGetMarketplaceChannels();

  const {
    printType,
    printTypeModalOpen,
    pendingPrintType,
    setPendingPrintType,
    handleSavePrintType,
    handleOpenPrintTypeModal,
  } = usePrintType();

  const [page, setPage] = useState(0);
  const [markPrintedEnabled, setMarkPrintedEnabled] = useState(false);

  const {
    data: ordersData,
    isLoading: ordersLoading,
    error: ordersError,
    isFetching,
  } = useGetMarketplaceOrders(page, filter, channelFilter);

  const {
    selectedOrder,
    setSelectedOrder,
    invoiceKey,
    orders,
    pagination,
    handlePrevPage,
    handleNextPage,
    handleSelectOrder,
    selectNextOrder,
  } = useOrderSelection(
    ordersData,
    filter,
    channelFilter,
    page,
    setPage
  );

  const {
    data: invoiceData,
    isLoading: invoiceLoading,
    error: invoiceError,
    refetch: refetchInvoiceData,
  } = useGetMarketplaceInvoice(invoiceKey, !!invoiceKey);

  const { 
    data: labelData, 
    error: labelApiError, 
    refetch: refetchLabel,
    isLoading: labelLoading 
  } = useGetMarketplaceInvoiceLabel(invoiceKey, labelFormat, !!invoiceKey);


  const {
    eanInputs,
    eanValidated,
    eanRefs,
    onEanInputChange,
    clearEanInputs,
    handleEanKeyDown,
  } = useOrderEanValidation(selectedOrder, invoiceData, printType);

  const PrintTypeModal = (
    <PrintTypeSelector
      open={printTypeModalOpen}
      pendingPrintType={pendingPrintType}
      setPendingPrintType={setPendingPrintType}
      onSave={handleSavePrintType}
    />
  );

  const {
    isConnected: bridgeConnected,
    printers: availablePrinters,
    selectedPrinter,
    isLoading: loadingPrinters,
    downloadProgress,
    loadPrinters: detectPrinters,
    selectPrinter,
    downloadBridgeInstaller,
    printLabel,
  } = useBridgeStatus();

  const {
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    showSnackbar,
    handleCloseSnackbar,
  } = useSnackbar();

  const {
    printerModalOpen,
    setPrinterModalOpen,
    bridgeInstallModalOpen,
    setBridgeInstallModalOpen,
    autoPrintEnabled,
    setAutoPrintEnabled,
    pendingAutoPrint,
    setPendingAutoPrint,
    isClosingPrinterModal,
    handleSelectPrinter,
    handleClosePrinterModal,
    handlePrintLabelWithPrinterCheck,
  } = useOrderPrinter(
    availablePrinters,
    selectedPrinter,
    selectPrinter,
    printLabel,
    showSnackbar
  );

  const {
    isPrinting,
    showForceDialog,
    labelError: printError,
    handlePrintLabel,
    handleCloseForceDialog,
    handleConfirmForceDialog,
  } = useOrderPrint({
    selectedOrder,
    printType,
    eanValidated,
    selectedPrinter,
    labelFormat,
    bridgeConnected,
    printLabel,
    setPrinterModalOpen: (open) => {
      if (!open || (!printerModalOpen && !isClosingPrinterModal)) {
        setPrinterModalOpen(open);
      }
    },
    refetchLabel,
    invoiceData,
    labelData,
    labelLoading,
    onPrintSuccess: async () => {
      if (selectedOrder && invoiceKey) {
        try {
          setMarkPrintedEnabled(true);
          await markOrderAsPrinted();
          setMarkPrintedEnabled(false);
          showSnackbar("Pedido marcado como impresso!", "success");
        } catch (error) {
          setMarkPrintedEnabled(false);
          showSnackbar("Erro ao marcar pedido como impresso", "error");
        }
        clearEanInputs();
      }
    },
  });

  const { refetch: markOrderAsPrinted } = useMarkInvoiceAsPrinted(
    invoiceKey, 
    markPrintedEnabled
  );

  useOrderPrintManager({
    selectedOrder,
    printType,
    eanValidated,
    isPrinting,
    printerModalOpen,
    autoPrintEnabled,
    bridgeConnected,
    selectedPrinter,
    handlePrintLabel,
    handlePrintLabelWithPrinterCheck,
    selectNextOrder,
  });


  const canPrint = () => {
    if (!selectedOrder || !invoiceData) {
      return false;
    }
    
    if (
      printType === "detalhada" &&
      invoiceData.shipping &&
      Array.isArray(invoiceData.shipping.items_gtin)
    ) {
      return invoiceData.shipping.items_gtin.every((item, idx) => {
        if (!item.gtin_code) return true;
        return eanValidated[idx] === true;
      });
    }
    
    return true;
  };

  const handlePrintWithReprintCheck = async () => {
    if (printError || labelApiError) {
      try {
        await refetchLabel();
        showSnackbar(
          "Tentando novamente. Clique em imprimir para continuar.",
          "info"
        );
        playBeep(200, 0.3, "error", "printError");
      } catch (error) {
        showSnackbar(
          "Erro ao tentar novamente. Por favor, tente mais tarde.",
          "error"
        );
        playBeep(200, 0.3, "error", "printError");
      }
      return;
    }

    if (
      selectedOrder &&
      selectedOrder.status === "printed" &&
      filter === "printed"
    ) {
      setPendingReprintOrder(selectedOrder);
      setReprintDialogOpen(true);
      return;
    }

    if (selectedPrinter && labelFormat === "zpl") {
      handlePrintLabel();
    } else {
      handlePrintLabelWithPrinterCheck(handlePrintLabel);
    }
  };


  useEffect(() => {
    if (invoiceError) {
      showSnackbar(
        "Erro ao carregar dados do pedido. Tente novamente.",
        "error"
      );
      playBeep(200, 0.3, "error", "invoiceError");
    }
  }, [invoiceError]);

  useEffect(() => {
    if (labelApiError) {
      showSnackbar("Erro ao gerar etiqueta. Tente novamente.", "error");
    }
  }, [labelApiError]);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 1.5, md: 2, lg: 3 },
        position: "relative",
        minHeight: "100vh",
        borderRadius: 2,
        boxShadow: "3px 3px 3px 3px #f78c002f",
        background: "linear-gradient(135deg, #fafbfc 0%, #f0f2f5 25%, #e8ebef 50%, #dde1e7 75%, #d1d7df 100%)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "radial-gradient(circle at 20% 50%, rgba(255, 152, 0, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 193, 7, 0.06) 0%, transparent 50%)",
          pointerEvents: "none",
          zIndex: 0
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(to right, transparent 92%, rgba(255, 152, 0, 0.04) 100%),
            linear-gradient(to bottom, transparent 92%, rgba(255, 152, 0, 0.04) 100%),
            linear-gradient(to left, transparent 92%, rgba(255, 152, 0, 0.03) 100%),
            linear-gradient(to top, transparent 92%, rgba(255, 152, 0, 0.03) 100%)
          `,
          pointerEvents: "none",
          zIndex: 0
        }
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <OrderFormContainer
          theme={theme}
          printType={printType}
          handleOpenPrintTypeModal={handleOpenPrintTypeModal}
          filter={filter}
          setFilter={setFilter}
          channelFilter={channelFilter}
          setChannelFilter={setChannelFilter}
          channelsData={channelsData}
          pagination={pagination}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, sm: 2, md: 3, lg: 4 },
          mt: 0.5,
          alignItems: "flex-start",
          position: "relative",
          zIndex: 1
        }}
      >
        <Box
          sx={{
            flex: {
              xs: "1 1 100%",
              md: isMobile ? "1 1 100%" : isTablet ? "0 0 42%" : "0 0 35%",
              lg: "0 0 30%"
            },
            minWidth: { xs: "100%", md: 300, lg: 350 },
            maxWidth: { md: isTablet ? "42%" : "35%", lg: "30%" }
          }}
        >
          <OrdersList
            orders={orders}
            ordersError={ordersError}
            ordersLoading={ordersLoading}
            isFetching={isFetching}
            pagination={pagination}
            selectedOrder={selectedOrder}
            handleSelectOrder={(order) => handleSelectOrder(order, invoiceError, refetchInvoiceData)}
            handlePrevPage={handlePrevPage}
            handleNextPage={handleNextPage}
            theme={theme}
            invoiceError={invoiceError}
            invoiceLoading={invoiceLoading}
          />
        </Box>

        <Box
          sx={{
            flex: {
              xs: "1 1 100%",
              md: isMobile ? "1 1 100%" : isTablet ? "1 1 58%" : "1 1 65%",
              lg: "1 1 70%"
            },
            minWidth: 0
          }}
        >
          <OrderDetails
            selectedOrder={selectedOrder}
            invoiceData={invoiceData}
            printType={printType}
            eanInputs={eanInputs}
            onEanInputChange={onEanInputChange}
            eanValidated={eanValidated}
            eanRefs={eanRefs}
            handleEanKeyDown={(idx, e) => handleEanKeyDown(idx, e, handlePrintWithReprintCheck, selectNextOrder)}
            theme={theme}
            invoiceLoading={invoiceLoading}
            printControls={null}
            bridgeAlert={
              selectedOrder && !invoiceError && invoiceData && (
                <PrinterStatusAlert
                  onInstallBridge={() => setBridgeInstallModalOpen(true)}
                  bridgeConnected={bridgeConnected}
                  onPrint={handlePrintWithReprintCheck}
                  onConfigurePrinter={() => setPrinterModalOpen(true)}
                  selectedPrinter={selectedPrinter}
                  labelLoading={isPrinting}
                  labelError={printError}
                  labelFormat={labelFormat}
                  onFormatChange={setLabelFormat}
                  disabled={!canPrint()}
                />
              )
            }
          />
        </Box>
      </Box>
      {PrintTypeModal}

      <OrderModals
        printerModalOpen={printerModalOpen}
        handleClosePrinterModal={handleClosePrinterModal}
        availablePrinters={availablePrinters}
        selectedPrinter={selectedPrinter}
        loadingPrinters={loadingPrinters}
        handleSelectPrinter={handleSelectPrinter}
        detectPrinters={detectPrinters}
        autoPrintEnabled={autoPrintEnabled}
        bridgeInstallModalOpen={bridgeInstallModalOpen}
        setBridgeInstallModalOpen={setBridgeInstallModalOpen}
        downloadProgress={downloadProgress}
        downloadBridgeInstaller={downloadBridgeInstaller}
        showForceDialog={showForceDialog}
        handleCloseForceDialog={handleCloseForceDialog}
        handleConfirmForceDialog={handleConfirmForceDialog}
        reprintDialogOpen={reprintDialogOpen}
        setReprintDialogOpen={setReprintDialogOpen}
        pendingReprintOrder={pendingReprintOrder}
        onReprintConfirm={() => {
          setReprintDialogOpen(false);
          if (selectedPrinter && labelFormat === "zpl") {
            handlePrintLabel();
          } else {
            handlePrintLabelWithPrinterCheck(handlePrintLabel);
          }
        }}
        snackbarOpen={snackbarOpen}
        handleCloseSnackbar={handleCloseSnackbar}
        snackbarSeverity={snackbarSeverity}
        snackbarMessage={snackbarMessage}
      />
    </Box>
  );
};

export default PrintOrderLabel;
